import React from 'react'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { internalRoleTypeTitles } from 'src/constants/internal-access/internalRoles'
import type { InternalAccessRole } from 'src/interfaces/internalAccess'
import { addS } from 'src/utils/baseUtils'
import { formatDateTime, parseFlexibleDateTime } from 'src/utils/grid'
import { resolveInternalRoleType } from 'src/utils/internalAccess/internalRoles'
import { InternalRoleTypeChip } from './InternalRoleTypeChip'

const formatPermissionCount = (role: InternalAccessRole): string => {
  if (role.allPermissions) {
    return 'All permissions'
  }

  return `${role.permissions.length} ${addS(role.permissions.length, 'permission')}`
}

/** `userCounts` is `null` without `internal_users_view` — the Users column is then left out. */
export const buildInternalRoleColumns = (
  userCounts: Record<string, number> | null,
): Array<GridColDef<InternalAccessRole>> => {
  const columns: Array<GridColDef<InternalAccessRole>> = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 200,
      hideable: false,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 140,
      display: 'flex',
      valueGetter: (value: unknown, row: InternalAccessRole) => {
        return internalRoleTypeTitles[resolveInternalRoleType(row)]
      },
      renderCell: (params: GridRenderCellParams<InternalAccessRole>) => {
        return <InternalRoleTypeChip role={params.row} />
      },
    },
    {
      field: 'permissions',
      headerName: 'Permissions',
      flex: 1,
      minWidth: 160,
      // Numeric so the column sorts by count; an all-permissions role sorts last.
      valueGetter: (value: unknown, row: InternalAccessRole) => {
        return row.allPermissions ? Number.POSITIVE_INFINITY : row.permissions.length
      },
      valueFormatter: (value: unknown, row: InternalAccessRole) => {
        return formatPermissionCount(row)
      },
    },
  ]

  if (userCounts) {
    columns.push({
      field: 'users',
      headerName: 'Users',
      width: 110,
      type: 'number',
      valueGetter: (value: unknown, row: InternalAccessRole) => {
        return userCounts[row.id] ?? 0
      },
    })
  }

  columns.push({
    field: 'createdAt',
    headerName: 'Created',
    flex: 1,
    minWidth: 180,
    valueGetter: parseFlexibleDateTime,
    valueFormatter: formatDateTime,
  })

  return columns
}
